/**
 * fetch-based, axios-compatible HTTP client.
 *
 * Drop-in replacement for the axios surface the console consumes through the
 * commons-js REST helper and the generated API clients: create(), the verb
 * methods, request(), axios-shaped responses/errors, and response interceptors.
 */

function isAbsoluteURL(url) {
    if (typeof url !== 'string') {
        return false;
    }
    if (url.startsWith('//')) {
        return true;
    }
    const schemeEnd = url.indexOf('://');
    return schemeEnd > 0 && !url.slice(0, schemeEnd).includes('/');
}

function stripEdgeSlashes(value, leading) {
    let result = value;
    while (leading ? result.startsWith('/') : result.endsWith('/')) {
        result = leading ? result.slice(1) : result.slice(0, -1);
    }
    return result;
}

function combineURLs(baseURL, relativeURL) {
    if (!baseURL) {
        return relativeURL || '';
    }
    if (!relativeURL) {
        return baseURL;
    }
    return `${stripEdgeSlashes(baseURL, false)}/${stripEdgeSlashes(relativeURL, true)}`;
}

function appendParams(url, params) {
    if (!params) {
        return url;
    }
    const search = new URLSearchParams();
    Object.entries(params).forEach(([ key, value ]) => {
        if (value === undefined || value === null) {
            return;
        }
        if (Array.isArray(value)) {
            value.forEach((item) => search.append(key, item));
        }
        else {
            search.append(key, value);
        }
    });
    const query = search.toString();
    if (!query) {
        return url;
    }
    return url + (url.includes('?') ? '&' : '?') + query;
}

function methodHasBody(method) {
    return method !== 'get' && method !== 'head';
}

function hasHeader(headers, name) {
    return Object.keys(headers).some((key) => key.toLowerCase() === name.toLowerCase());
}

function headersToObject(headers) {
    const result = {};
    if (headers && typeof headers.forEach === 'function') {
        headers.forEach((value, key) => {
            result[key] = value;
        });
    }
    return result;
}

function applyRequestBody(init, headers, config, method) {
    if (!methodHasBody(method) || config.data === undefined || config.data === null) {
        return;
    }
    const { data } = config;
    const isRawBody = typeof data === 'string'
        || data instanceof Blob
        || data instanceof FormData
        || data instanceof URLSearchParams;
    if (isRawBody) {
        init.body = data;
        return;
    }
    if (!hasHeader(headers, 'content-type')) {
        headers['Content-Type'] = 'application/json';
    }
    init.body = JSON.stringify(data);
}

async function parseResponseBody(response) {
    const text = await response.text();
    if (!text) {
        return text;
    }
    try {
        return JSON.parse(text);
    }
    catch {
        return text;
    }
}

async function performRequest(config) {
    const method = (config.method || 'get').toLowerCase();
    const baseTarget = isAbsoluteURL(config.url || '')
        ? config.url
        : combineURLs(config.baseURL, config.url);
    const target = appendParams(baseTarget, config.params);

    const headers = { ...config.headers };
    const init = {
        method: method.toUpperCase(),
        credentials: config.withCredentials ? 'include' : 'same-origin',
        headers,
    };
    applyRequestBody(init, headers, config, method);

    let rawResponse;
    try {
        rawResponse = await fetch(target, init);
    }
    catch (networkError) {
        networkError.config = config;
        networkError.isAxiosError = true;
        throw networkError;
    }

    const response = {
        data: await parseResponseBody(rawResponse),
        status: rawResponse.status,
        statusText: rawResponse.statusText,
        headers: headersToObject(rawResponse.headers),
        config,
    };

    if (!rawResponse.ok) {
        const error = new Error(`Request failed with status code ${rawResponse.status}`);
        error.isAxiosError = true;
        error.config = config;
        error.response = response;
        throw error;
    }

    return response;
}

function createInstance(instanceConfig = {}) {
    const requestInterceptors = [];
    const responseInterceptors = [];

    function request(config = {}) {
        const merged = {
            ...instanceConfig,
            ...config,
            headers: {
                ...instanceConfig.headers,
                ...config.headers,
            },
        };
        let configPromise = Promise.resolve(merged);
        // Axios runs request interceptors in reverse registration order (last registered runs first);
        // response interceptors run in registration order. Match that contract.
        requestInterceptors.slice().reverse().forEach(({ fulfilled, rejected }) => {
            configPromise = configPromise.then(fulfilled, rejected);
        });
        let promise = configPromise.then((finalConfig) => performRequest(finalConfig));
        responseInterceptors.forEach(({ fulfilled, rejected }) => {
            promise = promise.then(fulfilled, rejected);
        });
        return promise;
    }

    return {
        request,
        get: (url, config = {}) => request({
            ...config,
            method: 'get',
            url,
        }),
        delete: (url, config = {}) => request({
            ...config,
            method: 'delete',
            url,
        }),
        post: (url, data, config = {}) => request({
            ...config,
            method: 'post',
            url,
            data,
        }),
        put: (url, data, config = {}) => request({
            ...config,
            method: 'put',
            url,
            data,
        }),
        patch: (url, data, config = {}) => request({
            ...config,
            method: 'patch',
            url,
            data,
        }),
        interceptors: {
            request: {
                use(fulfilled, rejected) {
                    requestInterceptors.push({
                        fulfilled,
                        rejected,
                    });
                    return requestInterceptors.length - 1;
                },
            },
            response: {
                use(fulfilled, rejected) {
                    responseInterceptors.push({
                        fulfilled,
                        rejected,
                    });
                    return responseInterceptors.length - 1;
                },
            },
        },
    };
}

export default {
    create: createInstance,
};
